import * as React from 'react'
import { useMutation } from '@connectrpc/connect-query'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Paperclip, Image as ImageIcon, FileText, Download, Trash2 } from 'lucide-react'
import { getAttachment } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import type { Attachment, AttachmentLite } from '@/gen/cortado/v1/attachments_pb'
import { formatTimestampWithTime } from '@/lib/date-utils'
import { formatFullName } from '@/lib/utils'
import { getErrorDescription } from '@/lib/error-utils'
import { AttachmentPreview } from './attachment-preview'

interface AttachmentListProps {
  attachments: AttachmentLite[]
  onDelete?: (id: string) => void
}

function formatBytes(bytes: bigint | number): string {
  const n = typeof bytes === 'bigint' ? Number(bytes) : bytes
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function isPreviewableImage(contentType: string): boolean {
  return contentType === 'image/png' || contentType === 'image/jpeg'
}

function isPreviewable(contentType: string): boolean {
  return isPreviewableImage(contentType) || contentType === 'application/pdf'
}

function FileMedia({ attachment }: { attachment: AttachmentLite }) {
  if (isPreviewableImage(attachment.contentType) && attachment.downloadUrl) {
    return (
      <img
        src={attachment.downloadUrl}
        alt={attachment.filename}
        className="h-12 w-12 rounded object-cover border bg-muted"
        loading="lazy"
      />
    )
  }
  if (attachment.contentType === 'application/pdf') {
    return (
      <div className="h-12 w-12 rounded border bg-muted flex items-center justify-center">
        <FileText className="h-5 w-5 text-muted-foreground" />
      </div>
    )
  }
  if (attachment.contentType.startsWith('image/')) {
    return (
      <div className="h-12 w-12 rounded border bg-muted flex items-center justify-center">
        <ImageIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    )
  }
  return (
    <div className="h-12 w-12 rounded border bg-muted flex items-center justify-center">
      <Paperclip className="h-5 w-5 text-muted-foreground" />
    </div>
  )
}

export function AttachmentList({ attachments, onDelete }: AttachmentListProps) {
  const getMutation = useMutation(getAttachment)
  const [previewAttachment, setPreviewAttachment] = React.useState<Attachment | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<AttachmentLite | null>(null)

  const handlePreview = async (id: string) => {
    try {
      const response = await getMutation.mutateAsync({ id })
      const attachment = response.attachment
      if (!attachment?.downloadUrl) {
        toast.error('Preview link unavailable')
        return
      }
      setPreviewAttachment(attachment)
    } catch (error) {
      toast.error('Failed to get preview link', {
        description: getErrorDescription(error),
      })
    }
  }

  const handleDownload = async (id: string) => {
    try {
      const response = await getMutation.mutateAsync({ id })
      const url = response.attachment?.downloadUrl
      if (!url) {
        toast.error('Download link unavailable')
        return
      }
      window.open(url, '_blank', 'noopener')
    } catch (error) {
      toast.error('Failed to get download link', {
        description: getErrorDescription(error),
      })
    }
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget || !onDelete) return
    onDelete(deleteTarget.id)
    setDeleteTarget(null)
  }

  if (attachments.length === 0) {
    return (
      <Empty className="border border-dashed py-12">
        <EmptyMedia variant="icon">
          <Paperclip className="h-5 w-5" />
        </EmptyMedia>
        <EmptyDescription>No attachments yet</EmptyDescription>
      </Empty>
    )
  }

  return (
    <>
      <div className="space-y-3">
        {attachments.map((att) => {
          const createdByName = formatFullName(
            att.createdByFirstName,
            att.createdByLastName,
            'Unknown'
          )
          const createdAt = formatTimestampWithTime(att.createdAt)
          const canPreview = isPreviewable(att.contentType)

          return (
            <div
              key={att.id}
              role={canPreview ? 'button' : undefined}
              tabIndex={canPreview ? 0 : undefined}
              onClick={canPreview ? () => void handlePreview(att.id) : undefined}
              onKeyDown={
                canPreview
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        void handlePreview(att.id)
                      }
                    }
                  : undefined
              }
              className={`rounded-lg border p-4 hover:bg-muted/50 transition-colors group ${
                canPreview ? 'cursor-pointer' : ''
              }`}
            >
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <FileMedia attachment={att} />
                </div>

                <div className="flex-1 min-w-0 space-y-2">
                  <div
                    className="text-sm font-medium text-foreground truncate"
                    title={att.filename}
                  >
                    {att.filename}
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div>{formatBytes(att.sizeBytes)}</div>
                    <div>
                      <span>Uploaded by {createdByName}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 min-w-fit">
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDownload(att.id)
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      disabled={getMutation.isPending}
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {onDelete && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteTarget(att)
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground text-right">
                    {createdAt}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <AttachmentPreview
        attachment={previewAttachment}
        open={!!previewAttachment}
        onOpenChange={(open) => {
          if (!open) setPreviewAttachment(null)
        }}
      />
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attachment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>{deleteTarget?.filename}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              variant="destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
